#!/usr/bin/env node

import { Command } from "commander";
import chalk from "chalk";
import { init } from "./cli/init";
import { validate } from "./cli/validate";
import { sync } from "./cli/sync";
import { check } from "./cli/check";
import { generate } from "./cli/generate";
import { generateSchema } from "./cli/generate-schema";
import { promoteEnv } from "./cli/promote-env";
import {
  setSecretCommand,
  getSecretCommand,
  decryptSecretsCommand,
  keygenCommand,
  exportKeyCommand,
  importKeyCommand,
  addUserCommand,
  revokeUserCommand,
  listRevokedCommand,
  recoverCommand,
  setRecoveryKeyCommand,
} from "./secrets/cli-commands";
import { mergeSecretsCommand } from "./secrets/merge-helper";

const program = new Command();

program
  .name("envfortress")
  .description(
    "Safe, type-checked environment variable handling for React, Vite, and Node"
  )
  .version("1.0.0");

program
  .command("init")
  .description("Initialize a new environment configuration")
  .option(
    "-f, --framework <framework>",
    "Framework to use (nextjs, vite, cra, express)",
    "vite"
  )
  .option("-o, --output <file>", "Output file path", "src/env.ts")
  .option("--yes", "Skip prompts and use defaults")
  .action(async (options) => {
    try {
      await init(options);
    } catch (error) {
      console.error(
        chalk.red("Error:"),
        error instanceof Error ? error.message : String(error)
      );
      process.exit(1);
    }
  });

program
  .command("validate")
  .description("Validate your current .env file against your schema")
  .option("-s, --schema <file>", "Schema file path", "src/env.ts")
  .option("-e, --env <file>", ".env file path", ".env")
  .option("--strict", "Enable strict mode")
  .option("--debug", "Enable debug mode")
  .option("--ci", "Enable CI/build mode (strict, no debug, exit 1 on error)")
  .action(async (options) => {
    try {
      await validate(options);
    } catch (error) {
      console.error(
        chalk.red("Error:"),
        error instanceof Error ? error.message : String(error)
      );
      process.exit(1);
    }
  });

program
  .command("sync")
  .description("Sync .env.example with your schema")
  .option("-s, --schema <file>", "Schema file path", "src/env.ts")
  .option("-e, --example <file>", ".env.example file path", ".env.example")
  .option("--force", "Overwrite existing .env.example file")
  .action(async (options) => {
    try {
      await sync(options);
    } catch (error) {
      console.error(
        chalk.red("Error:"),
        error instanceof Error ? error.message : String(error)
      );
      process.exit(1);
    }
  });

program
  .command("check")
  .description("Check environment variables in current process")
  .option("-s, --schema <file>", "Schema file path", "src/env.ts")
  .option("--strict", "Enable strict mode")
  .option("--debug", "Enable debug mode")
  .action(async (options) => {
    try {
      await check(options);
    } catch (error) {
      console.error(
        chalk.red("Error:"),
        error instanceof Error ? error.message : String(error)
      );
      process.exit(1);
    }
  });

program
  .command("generate")
  .description("Generate TypeScript types from your schema")
  .option("-s, --schema <file>", "Schema file path", "src/env.ts")
  .option("-o, --output <file>", "Output file path", "src/env.types.ts")
  .action(async (options) => {
    try {
      await generate(options);
    } catch (error) {
      console.error(
        chalk.red("Error:"),
        error instanceof Error ? error.message : String(error)
      );
      process.exit(1);
    }
  });

program
  .command("generate-schema")
  .description("Generate a TypeScript Zod schema from an existing .env file")
  .option("-e, --env <file>", ".env file path", ".env")
  .option("-o, --output <file>", "Output TypeScript schema file", "src/env.ts")
  .option(
    "--client-prefix <prefix>",
    "Prefix for client variables (comma-separated)",
    "VITE_,NEXT_PUBLIC_,REACT_APP_"
  )
  .option("--guess-types", "Infer types from values", false)
  .option("--non-interactive", "Skip prompts and use inferred types", false)
  .option(
    "--sync",
    "Sync with existing schema, preserving manual edits and comments",
    false
  )
  .action(async (options) => {
    try {
      await generateSchema(options);
    } catch (error) {
      console.error(
        chalk.red("Error:"),
        error instanceof Error ? error.message : String(error)
      );
      process.exit(1);
    }
  });

program
  .command("promote-env")
  .description(
    "Promote or sync variables from one .env file to another (e.g., development to production or example)"
  )
  .option("--from <file>", "Source .env file", ".env.development")
  .option("--to <file>", "Target .env file", ".env.production")
  .option(
    "--mode <mode>",
    "Promotion mode: add (default), remove, comment, example",
    "add"
  )
  .option("--interactive", "Prompt before making changes", false)
  .option("--dry-run", "Show what would change without writing", false)
  .option(
    "--backup",
    "Create a backup of the target file before writing",
    false
  )
  .action(async (options) => {
    try {
      await promoteEnv(options);
    } catch (error) {
      console.error(
        chalk.red("Error:"),
        error instanceof Error ? error.message : String(error)
      );
      process.exit(1);
    }
  });

const secrets = new Command("secrets").description("Team secrets management");

secrets
  .command("set")
  .description("Set an encrypted secret")
  .argument("<key>", "Secret key")
  .argument("<value>", "Secret value")
  .requiredOption(
    "--recipients <recipients...>",
    "Recipient public keys (space-separated)"
  )
  .action(async (key, value, options) => {
    try {
      await setSecretCommand(key, value, options);
    } catch (error) {
      console.error(
        chalk.red("Error:"),
        error instanceof Error ? error.message : String(error)
      );
      process.exit(1);
    }
  });

secrets
  .command("get")
  .description("Get and decrypt a secret")
  .argument("<key>", "Secret key")
  .requiredOption("--identity <identity>", "Private key (age identity)")
  .action(async (key, options) => {
    try {
      await getSecretCommand(key, options);
    } catch (error) {
      console.error(
        chalk.red("Error:"),
        error instanceof Error ? error.message : String(error)
      );
      process.exit(1);
    }
  });

secrets
  .command("decrypt")
  .description("Decrypt all secrets for local use")
  .requiredOption("--identity <identity>", "Private key (age identity)")
  .option("--output <file>", "Output file", ".env")
  .action(async (options) => {
    try {
      await decryptSecretsCommand(options);
    } catch (error) {
      console.error(
        chalk.red("Error:"),
        error instanceof Error ? error.message : String(error)
      );
      process.exit(1);
    }
  });

secrets
  .command("keygen")
  .description("Generate and store a new age identity (private key)")
  .option(
    "--secure-store <backend>",
    "Storage backend: file (default) or env",
    "file"
  )
  .option("--path <file>", "File path for storing the identity")
  .action(async (options) => {
    try {
      await keygenCommand(options);
    } catch (error) {
      console.error(
        chalk.red("Error:"),
        error instanceof Error ? error.message : String(error)
      );
      process.exit(1);
    }
  });

secrets
  .command("export-key")
  .description("Export the stored identity to a file or stdout")
  .option("--from <backend>", "Source backend: file (default) or env", "file")
  .option("--path <file>", "File path to read the identity from")
  .option("--output <file>", "Output file (default: stdout)")
  .action(async (options) => {
    try {
      await exportKeyCommand(options);
    } catch (error) {
      console.error(
        chalk.red("Error:"),
        error instanceof Error ? error.message : String(error)
      );
      process.exit(1);
    }
  });

secrets
  .command("import-key")
  .description("Import an identity from a file or string and store it")
  .option(
    "--secure-store <backend>",
    "Storage backend: file (default) or env",
    "file"
  )
  .option("--input <string>", "Identity string to import")
  .option("--input-file <file>", "File to read the identity from")
  .option("--path <file>", "File path for storing the identity")
  .action(async (options) => {
    try {
      await importKeyCommand(options);
    } catch (error) {
      console.error(
        chalk.red("Error:"),
        error instanceof Error ? error.message : String(error)
      );
      process.exit(1);
    }
  });

secrets
  .command("add-user")
  .description("Add a user to the team config")
  .argument("<email>", "User email")
  .requiredOption("--pubkey <key>", "User's public key")
  .action(async (email, options) => {
    try {
      await addUserCommand(email, options.pubkey, options);
    } catch (error) {
      console.error(
        chalk.red("Error:"),
        error instanceof Error ? error.message : String(error)
      );
      process.exit(1);
    }
  });

secrets
  .command("revoke-user <email>")
  .description("Revoke (remove) a user from the team and add to revoked list")
  .action((email, options) => revokeUserCommand(email, options));

secrets
  .command("list-revoked")
  .description("List revoked users and timestamps")
  .action((options) => listRevokedCommand(options));

secrets
  .command("recover")
  .description("Show recovery instructions and check for recovery key")
  .action((options) => recoverCommand(options));

secrets
  .command("set-recovery-key <email> <pubkey>")
  .description(
    "Set the recovery admin/service key (always included as a recipient)"
  )
  .action((email, pubkey, options) =>
    setRecoveryKeyCommand(email, pubkey, options)
  );

secrets
  .command("merge")
  .description(
    "3-way merge of encrypted secrets files (per-secret conflict resolution)"
  )
  .requiredOption("--base <base>", "Base secrets file")
  .requiredOption("--ours <ours>", "Our secrets file")
  .requiredOption("--theirs <theirs>", "Their secrets file")
  .requiredOption("--output <output>", "Output merged secrets file")
  .requiredOption(
    "--identity <identity>",
    "Private key (age identity) for decryption"
  )
  .option(
    "--recipients <recipients>",
    "Comma-separated list of recipient public keys for the merged file"
  )
  .action(async (opts) => {
    await mergeSecretsCommand({
      base: opts.base,
      ours: opts.ours,
      theirs: opts.theirs,
      output: opts.output,
      identity: opts.identity,
      recipients: opts.recipients ? opts.recipients.split(",") : [],
    });
  });

program.addCommand(secrets);

program.parse();
